import { getJourneyData } from '../../api/journeyService';
import {
  getResumeRouteFromJourney,
  getResumeStateFromJourney,
  hydrateStoreFromJourney,
} from './journeyResume';
import { setStoredApplicationNumber, setStoredCaseId } from './termJourneyStorage';
import { logError } from './logger';

const RESUME_DOB_SESSION_KEY = 'rnlic_resumeDob';

const routeRank = {
  '/': 0,
  '/home': 0,
  '/term-plan': 0,
  '/quote': 1,
  '/summary': 2,
  '/verification': 3,
  '/proposal': 4,
  '/proposal-review': 5,
};

const stripTrailingSlashes = (path) => {
  let clean = path;
  while (clean.length > 1 && clean.endsWith('/')) {
    clean = clean.slice(0, -1);
  }
  return clean;
};

const normalizePath = (path) => {
  if (!path) return '';
  const clean = stripTrailingSlashes(String(path).split('?')[0]);
  return clean || '/';
};

const readStoredResumeDob = (applicationNumber) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const scopedKey = applicationNumber
      ? `${RESUME_DOB_SESSION_KEY}:${String(applicationNumber).trim()}`
      : RESUME_DOB_SESSION_KEY;
    const value =
      (applicationNumber ? window.sessionStorage.getItem(scopedKey) : null) ||
      window.sessionStorage.getItem(RESUME_DOB_SESSION_KEY);
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
};

const hasProgressedBeyondCurrentRoute = (currentPath, resumeRoute) => {
  const currentRank = routeRank[normalizePath(currentPath)];
  const nextRank = routeRank[normalizePath(resumeRoute)];
  if (nextRank == null) return false;
  if (currentRank == null) return true;
  return nextRank > currentRank;
};

export async function redirectToLatestJourneyStage({
  applicationNumber,
  resumeDob,
  dispatch,
  navigate,
  getState,
  currentPath,
}) {
  const appNumber = String(applicationNumber || '').trim();
  if (!appNumber || typeof dispatch !== 'function' || typeof navigate !== 'function') {
    return false;
  }

  try {
    const journey = await getJourneyData(
      appNumber,
      resumeDob || readStoredResumeDob(appNumber) || undefined,
    );
    if (!journey || typeof journey !== 'object') return false;

    hydrateStoreFromJourney(dispatch, journey, appNumber, getState);

    const journeyAppNumber =
      journey.applicationNumber ||
      journey.application_number ||
      appNumber;
    const journeyCaseId =
      journey.caseId ||
      journey.c_caseId ||
      journey.case_number ||
      null;
    if (journeyAppNumber) setStoredApplicationNumber(journeyAppNumber);
    if (journeyCaseId) setStoredCaseId(journeyCaseId);

    const resumeRoute = getResumeRouteFromJourney(journey);
    if (!resumeRoute || !hasProgressedBeyondCurrentRoute(currentPath, resumeRoute)) {
      return false;
    }

    const resumeState =
      resumeRoute === '/proposal' ? getResumeStateFromJourney(journey) : null;
    navigate(resumeRoute, resumeState ? { state: resumeState } : {});
    return true;
  } catch (error) {
    logError('[journeyStageGuard] Failed to check latest journey stage', error);
    return false;
  }
}
